import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { TOUR_MODULES } from "./steps";

// Lightweight, module-aware tour state.
//   • start(moduleKey)      — open the tour at the first step of a module
//   • goToModule(moduleKey) — hand off to another module, staying active
//   • next / back / close   — within the active module
// No persistence, no completion flag, no auto-launch — the "User Guide" button
// on each screen is the only entry point.
const TourContext = createContext(null);

const DEFAULT_MODULE = "dashboard";
const resolveModule = (key) => (key && TOUR_MODULES[key] ? key : DEFAULT_MODULE);

export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [moduleKey, setModuleKey] = useState(DEFAULT_MODULE);
  const [stepIndex, setStepIndex] = useState(0);
  const launcherRef = useRef(null); // element to restore focus to on close

  const moduleConfig = TOUR_MODULES[moduleKey] || TOUR_MODULES[DEFAULT_MODULE];
  const stepCount = moduleConfig.steps.length;

  const start = useCallback((key) => {
    launcherRef.current = document.activeElement;
    setModuleKey(resolveModule(key));
    setStepIndex(0);
    setActive(true);
  }, []);

  const goToModule = useCallback((key) => {
    if (!key || !TOUR_MODULES[key]) {
      setActive(false);
      return;
    }
    setModuleKey(key);
    setStepIndex(0);
    setActive(true);
  }, []);

  const close = useCallback(() => {
    setActive(false);
    const el = launcherRef.current;
    if (el && typeof el.focus === "function") {
      // defer so the overlay has unmounted first
      requestAnimationFrame(() => el.focus({ preventScroll: true }));
    }
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= stepCount - 1) {
        setActive(false);
        return 0;
      }
      return i + 1;
    });
  }, [stepCount]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  return (
    <TourContext.Provider
      value={{ active, moduleKey, moduleConfig, stepIndex, stepCount, start, close, next, back, goToModule }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within a TourProvider");
  return ctx;
}
